import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Link2,
  X,
  Anchor,
  ClipboardList,
  Zap,
  ChevronLeft,
  ChevronRight,
  Tags,
  Search,
  Upload,
  Download,
  CheckCircle2,
  Workflow,
} from 'lucide-react'
import {
  type CgtFieldType,
  type FieldDefinition,
  type MrFieldKey,
  MR_FIELD_KEYS,
  MR_FIELD_LABELS,
  supportsAttachment,
  normalizeAttachment,
  attachmentMode,
  ATTACHMENT_MODES,
  ATTACHMENT_MODE_LABELS,
  type AttachmentMode,
} from '@/types/cgt'
import { fieldLibraryApi, type CreateLibraryFieldPayload } from '@/api/fieldLibraryApi'
import { supportsWriteOnce } from '@/lib/fieldLock'
import { formatSystemName, resolveSystemName } from '@/lib/systemName'
import { useTableColumns } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { sortParam } from '@/lib/tableSort'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { useFieldLibrary, useFieldUsage } from '@/hooks/useFieldLibrary'
import { useProcessStepList } from '@/hooks/useProcessSteps'
import {
  buildLibraryFieldImportTemplate,
  buildLibraryFieldExport,
  parseLibraryFieldWorkbook,
  type KnownFormForImport,
  type LibraryFieldImportRow,
} from '@/lib/formExcel'
import { formsApi } from '@/api/formsApi'
import { allFields } from '@/lib/formSections'
import { useFieldCategories, useFieldCategoriesList } from '@/hooks/useFieldCategories'
import type { FieldCategory } from '@/api/fieldCategoriesApi'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { useFeatureFlag } from '@/providers/FeatureFlagsProvider'
import { extractFormulaRefs, type DisplayFormat } from '@/lib/formula'
import { LibraryFormulaBuilder } from '@/components/form-builder/LibraryFormulaBuilder'
import { MultiSelect } from '@/components/form-builder/MultiSelect'
import { SingleSelect } from '@/components/form-builder/SingleSelect'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PageHeading } from '@/components/layout/PageHeading'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

/**
 * Columns this table offers, and which of them the user may hide.
 *
 * Module-level so it is referentially stable — `useTableColumns` memoizes on it
 * (see that hook's docblock). The `tableId` below is a released identifier:
 * changing it abandons every saved layout for this table.
 *
 * `label` and `actions` are locked. The label is how a row is identified at all,
 * and hiding the actions cell would leave no way to edit or delete a field —
 * neither is a layout preference, so neither is offered.
 *
 * Categories is absent on purpose: it is already gated on a permission plus a
 * feature flag, so it is appended conditionally below rather than declared here.
 */
const FIELD_LIBRARY_TABLE_ID = 'field-library'

// `sortable` columns sort SERVER-side (z8nrz7e4h6): this list is paged, so the
// ids here are the backend's allowed sort keys (FIELD_SORTS in
// field-library.service). Flags and Categories hold several values per row, so
// they have no single order and are not sortable.
const FIELD_LIBRARY_COLUMNS: TableColumnDef[] = [
  { id: 'label', header: 'Label', locked: true, defaultWidth: 220, sortable: true },
  // The name formulas address this field by, and the value the import sheet's
  // "Field ID" column wrote (z8nrz7dt4b AC3 — the ID has to be visible and
  // unchanged after an import, or an author has no way to confirm it stuck).
  { id: 'refName', header: 'Field ID', defaultWidth: 160, sortable: true },
  { id: 'systemName', header: 'System name', defaultWidth: 180, sortable: true },
  { id: 'definition', header: 'Definition', defaultWidth: 280, sortable: true },
  { id: 'example', header: 'Example', defaultWidth: 140, sortable: true },
  { id: 'fieldType', header: 'Type', defaultWidth: 120, sortable: true },
  { id: 'required', header: 'Required', defaultWidth: 110, sortable: true },
  { id: 'flags', header: 'Flags', defaultWidth: 160 },
  { id: 'enabled', header: 'Enabled', defaultWidth: 100, sortable: true },
]

const CATEGORIES_COLUMN: TableColumnDef = {
  id: 'categories',
  header: 'Categories',
  defaultWidth: 200,
}

// 4 icon buttons at 40px + gaps + px-4 padding.
const ACTIONS_COLUMN: TableColumnDef = {
  id: 'actions',
  header: 'Actions',
  locked: true,
  defaultWidth: 200,
}

// Categories are a small admin-defined set — fetch them all for pickers/filter/tags.
const CATEGORY_TAKE = 200

const FIELD_TYPE_OPTIONS: { value: CgtFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'calculated', label: 'Calculated' },
  { value: 'signature', label: 'Signature' },
  { value: 'process', label: 'Process' },
  { value: 'attachment', label: 'Attachment' },
]

const FIELD_TYPE_LABELS: Record<CgtFieldType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  datetime: 'Date & time',
  textarea: 'Textarea',
  checkbox: 'Checkbox',
  dropdown: 'Dropdown',
  calculated: 'Calculated',
  signature: 'Signature',
  process: 'Process',
  attachment: 'Attachment',
  // Layout-only / composite / form-only types — not creatable as standalone
  // library fields (the backend LibraryField enum has the 9 above), but
  // labelled for display. `image` joins `datetime` here: both are placed from
  // the builder palette and live in the form definition's JSONB, which takes
  // any field type, rather than in the Prisma enum.
  table: 'Table',
  section: 'Section',
  image: 'Image',
  description: 'Description',
  review: 'Review',
}

// ─── Draft model for the create/edit form ─────────────────────────────────────

interface FieldDraft {
  label: string
  fieldType: CgtFieldType
  required: boolean
  enabled: boolean
  helpText: string
  definition: string
  example: string
  options: string[]
  formula: string
  displayFormat: 'number' | 'decimal2' | 'decimal3' | 'date' | 'datetime'
  requiredGroup: string
  anchored: boolean
  canChange: boolean
  isMrField: boolean
  mrFieldKey: MrFieldKey | ''
  categoryIds: string[]
  attachment: boolean
  attachmentRequired: boolean
}

function emptyDraft(): FieldDraft {
  return {
    label: '',
    fieldType: 'text',
    required: false,
    enabled: true,
    helpText: '',
    definition: '',
    example: '',
    options: ['Option 1', 'Option 2'],
    formula: '',
    displayFormat: 'number',
    requiredGroup: '',
    anchored: false,
    canChange: true,
    isMrField: false,
    mrFieldKey: '',
    categoryIds: [],
    attachment: false,
    attachmentRequired: false,
  }
}

function draftFromField(field: FieldDefinition): FieldDraft {
  return {
    label: field.label,
    fieldType: field.fieldType,
    required: field.required,
    enabled: field.enabled ?? true,
    helpText: field.helpText ?? '',
    definition: field.definition ?? '',
    example: field.example ?? '',
    options: field.options && field.options.length > 0 ? field.options : ['Option 1'],
    formula: field.formula ?? '',
    displayFormat: field.displayFormat ?? 'number',
    requiredGroup: field.requiredGroup ?? '',
    anchored: field.anchored ?? false,
    // Absent means changeable — never read `undefined` as locked.
    canChange: field.canChange !== false,
    isMrField: field.isMrField ?? false,
    mrFieldKey: field.mrFieldKey ?? '',
    categoryIds: field.categoryIds ?? [],
    attachment: field.attachment?.enabled ?? false,
    attachmentRequired: field.attachment?.required ?? false,
  }
}

/**
 * Builds a complete, explicit payload for both create (POST) and update (PUT).
 *
 * The backend PUT is a partial merge — absent keys are left unchanged — so we
 * must always send an explicit value for every key so that toggling things OFF
 * (e.g. a fieldType change that clears options) is actually persisted.
 */
function draftToPayload(draft: FieldDraft): CreateLibraryFieldPayload {
  const isCalculated = draft.fieldType === 'calculated'
  const isDropdown = draft.fieldType === 'dropdown'
  const isSignature = draft.fieldType === 'signature'
  const isMr = isMrEligibleType(draft.fieldType) && draft.isMrField
  const trimmedFormula = draft.formula.trim()
  return {
    label: draft.label.trim(),
    fieldType: draft.fieldType,
    // MR fields are read-only (auto-populated) so they can never be required.
    required: isCalculated || isMr ? false : draft.required,
    helpText: draft.helpText.trim() || undefined,
    // Sent as '' rather than undefined when emptied: PUT is a partial merge, so
    // `undefined` would leave the old text in place and the dialog would keep
    // showing a definition the author had just cleared. The backend normalizes
    // the blank to NULL.
    definition: draft.definition.trim(),
    example: draft.example.trim(),
    options: isDropdown ? draft.options.map(o => o.trim()).filter(Boolean) : [],
    formula: isCalculated ? trimmedFormula : '',
    formulaRefs: isCalculated && trimmedFormula ? extractFormulaRefs(draft.formula) : [],
    displayFormat: isCalculated ? draft.displayFormat : undefined,
    requiredGroup: isSignature ? draft.requiredGroup.trim() : '',
    anchored: draft.anchored,
    // Explicit both ways. PUT is a partial merge (see this function's docblock),
    // so sending `undefined` for `true` left an existing write-once field stuck
    // at `canChange: false` — the UI said "Stays editable" while the backend
    // went on rejecting edits.
    canChange: draft.canChange,
    enabled: draft.enabled,
    isMrField: isMr,
    // The backend clears mrFieldKey whenever isMrField is false, so omitting the
    // key here when the toggle is off still persists the clear on PUT.
    mrFieldKey: isMr && draft.mrFieldKey ? draft.mrFieldKey : undefined,
    categoryIds: draft.categoryIds,
    // normalizeAttachment drops it for ineligible types and clears `required`
    // when the attachment is off, so PUT can never persist an orphaned flag.
    attachment: normalizeAttachment(draft.fieldType, {
      enabled: draft.attachment,
      required: draft.attachmentRequired,
    }),
  }
}

/** MR binding is only meaningful on text/date fields (names → text, DOB → date). */
function isMrEligibleType(fieldType: CgtFieldType): boolean {
  // Structural/special types that cannot hold a plain patient-demographic value.
  const ineligible: CgtFieldType[] = ['calculated', 'signature', 'process', 'section', 'table', 'attachment']
  return !ineligible.includes(fieldType)
}

// ─── System name (read-only, curated in the DB) ───────────────────────────────

const PROVISIONAL_HINT =
  'Provisional — derived from the label because no system name has been assigned yet. It will change if the label is renamed, so do not integrate against it.'

/**
 * Hover text for a system name: the identifier itself, which the cell no longer
 * spells out.
 *
 * The column shows the human form ("Donor Type"), so the machine form
 * ("donor_type") has to stay reachable somewhere — this is where anyone
 * integrating, or reading a migration, gets it back.
 */
function systemNameTitle(name: string, provisional: boolean): string {
  return provisional ? `${name}\n\n${PROVISIONAL_HINT}` : name
}

/** The system-name table cell: curated name plain, provisional name marked. */
function SystemNameCell({ field }: { field: FieldDefinition }) {
  const { name, provisional } = resolveSystemName(field)
  // A field with no label has nothing to derive from either. The create form
  // refuses a blank label, so this is only reachable for legacy rows.
  if (!name) return <span className='text-muted-foreground'>—</span>
  const shown = formatSystemName(name, field.label)
  if (!provisional) return <span title={systemNameTitle(name, provisional)}>{shown}</span>
  return (
    <span className='italic text-muted-foreground' title={systemNameTitle(name, provisional)}>
      {shown}
      <span className='ml-1 not-italic'>*</span>
    </span>
  )
}

/** The read-only system-name panel in the create/edit dialog. */
function SystemNameField({ field }: { field: FieldDefinition }) {
  const { name, provisional } = resolveSystemName(field)
  if (!name) return null
  return (
    <div className='flex flex-col gap-1.5'>
      <Label>System name</Label>
      <p
        className={`rounded-md border bg-muted/40 px-3 py-2 font-mono text-sm ${
          provisional ? 'italic text-muted-foreground' : ''
        }`}
        title={systemNameTitle(name, provisional)}
      >
        {formatSystemName(name, field.label)}
      </p>
      <p className='text-xs text-muted-foreground'>
        {provisional
          ? 'Not assigned yet — this is a provisional name derived from the label. The CGT team sets the real one; it is never editable here.'
          : 'Set by the CGT team and not editable here.'}
      </p>
    </div>
  )
}

// ─── Usage summary (shared by edit + delete) ──────────────────────────────────

function UsageSummary({ fieldId }: { fieldId: string }) {
  const usage = useFieldUsage(fieldId)
  if (usage.isLoading) {
    return <p className='text-xs text-muted-foreground'>Checking form usage…</p>
  }
  if (usage.isError || !usage.data) {
    return <p className='text-xs text-muted-foreground'>Could not load form usage.</p>
  }
  if (usage.data.count === 0) {
    return <p className='text-xs text-muted-foreground'>Not used by any form yet.</p>
  }
  return (
    <div className='rounded-md border bg-muted/40 px-3 py-2'>
      <p className='text-xs font-medium'>
        Used by {usage.data.count} form{usage.data.count === 1 ? '' : 's'}
      </p>
      <ul className='mt-1 space-y-0.5'>
        {usage.data.forms.map(f => (
          <li key={f.id} className='text-xs text-muted-foreground'>
            {f.name} <span className='opacity-70'>v{f.version}</span>
          </li>
        ))}
      </ul>
      <p className='mt-1.5 text-[11px] text-muted-foreground'>
        Fields are copied by value — existing forms keep their copy and are not changed.
      </p>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function FieldLibraryPage() {
  const { can } = usePermission()
  const canRead = can(PERMISSIONS.fieldsRead)
  const canCreate = can(PERMISSIONS.fieldsCreate)
  const canEdit = can(PERMISSIONS.fieldsEdit)
  const canDelete = can(PERMISSIONS.fieldsDelete)
  const fieldCategoriesEnabled = useFeatureFlag('fieldCategories')
  const canReadCategories = can(PERMISSIONS.fieldCategoriesRead) && fieldCategoriesEnabled
  const canManageCategories =
    can(PERMISSIONS.fieldCategoriesManage) && fieldCategoriesEnabled

  // Search / filter / pagination
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [fieldTypeFilter, setFieldTypeFilter] = useState<CgtFieldType | ''>('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  // Default hides disabled fields ("out of the way"); toggling on shows all.
  const [showDisabled, setShowDisabled] = useState(false)
  const [skip, setSkip] = useState(0)
  const [take, setTake] = useState(25)

  // Debounce q and reset to page 0 on new search
  useEffect(() => {
    // Guard prevents the mount-time timer from resetting pagination 300ms in
    // (same pattern as PatientsOverviewPage).
    if (q === debouncedQ) return
    const t = setTimeout(() => {
      setDebouncedQ(q)
      setSkip(0)
    }, 300)
    return () => clearTimeout(t)
  }, [q, debouncedQ])

  useEffect(() => { setSkip(0) }, [take])

  // Per-user column layout. The Categories column only exists for users who can
  // read categories, so the declared set is permission-dependent and has to be
  // memoized rather than passed inline.
  const columnDefs = useMemo(
    () =>
      canReadCategories
        ? [...FIELD_LIBRARY_COLUMNS, CATEGORIES_COLUMN, ACTIONS_COLUMN]
        : [...FIELD_LIBRARY_COLUMNS, ACTIONS_COLUMN],
    [canReadCategories],
  )
  const cols = useTableColumns(FIELD_LIBRARY_TABLE_ID, columnDefs)

  // A new sort is a new ordering of the whole list, so page 1 again (z8nrz7e4h6).
  // Reset during render, not in an effect, so the request never goes out for
  // the old page under the new order.
  const sort = sortParam(cols.sort)
  const [sortOfPage, setSortOfPage] = useState(sort)
  if (sortOfPage !== sort) {
    setSortOfPage(sort)
    setSkip(0)
  }

  const { list, create, update, remove, importFields, toggleEnabled } = useFieldLibrary({
    q: debouncedQ || undefined,
    fieldType: fieldTypeFilter || undefined,
    categoryId: categoryFilter || undefined,
    enabled: showDisabled ? undefined : true,
    // The e2e suite's own rows are not part of anyone's field library. An e2e
    // run still sees them — the harness stamps X-E2E-Test and the server drops
    // the filter for it.
    isTestData: false,
    skip,
    take,
    sort,
  })

  // Categories — used for the list filter, per-field tags, and the create/edit picker.
  // enabled=canReadCategories suppresses the request for users who lack the permission.
  const categoriesList = useFieldCategoriesList({ take: CATEGORY_TAKE }, canReadCategories)
  const categories = categoriesList.data?.items ?? []
  const categoryNameById = new Map(categories.map(c => [c.id, c.name]))

  // Process fields that already have a configured connection (any child row).
  // Powers the "Process" indicator badge on process-type rows.
  const processStepList = useProcessStepList()
  const configuredProcessFieldIds = useMemo(() => {
    const set = new Set<string>()
    for (const row of processStepList.data ?? []) {
      const c = row._count
      if (c.equipmentRequirements > 0 || c.inventoryRequirements > 0 || c.staffingLines > 0) {
        set.add(row.fieldId)
      }
    }
    return set
  }, [processStepList.data])

  // Derived pagination / facet data
  const items = list.data?.items ?? []
  const total = list.data?.total ?? 0
  const typeCounts = list.data?.typeCounts ?? {}
  const allCount = Object.values(typeCounts).reduce<number>((acc, n) => acc + n, 0)

  // Clamp skip when a deletion strands us on a phantom empty page
  useEffect(() => {
    if (total > 0 && skip >= total) {
      setSkip(Math.floor((total - 1) / take) * take)
    }
  }, [total])

  // Dialog state
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<FieldDefinition | null>(null)
  const [deleting, setDeleting] = useState<FieldDefinition | null>(null)
  const [usageFor, setUsageFor] = useState<FieldDefinition | null>(null)
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)

  // Per-dialog server errors shown inside each dialog (not behind it)
  const [createServerError, setCreateServerError] = useState<string | null>(null)
  const [editServerError, setEditServerError] = useState<string | null>(null)
  const [deleteServerError, setDeleteServerError] = useState<string | null>(null)
  // Per-row error for the inline Enabled switch, keyed by field id.
  const [enabledToggleErrors, setEnabledToggleErrors] = useState<Record<string, string>>({})

  // Import dialog state
  const [importParsed, setImportParsed] = useState<LibraryFieldImportRow[] | null>(null)
  const [importParseErrors, setImportParseErrors] = useState<string[]>([])
  const [importParseWarnings, setImportParseWarnings] = useState<string[]>([])
  const [importServerErrors, setImportServerErrors] = useState<string[]>([])
  const [importSuccess, setImportSuccess] = useState<{ created: number; updated: number } | null>(null)
  const [importTemplateLoading, setImportTemplateLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [exportNotices, setExportNotices] = useState<string[]>([])
  const importFileRef = useRef<HTMLInputElement>(null)

  // Both export (multi-page fetch + client-side workbook build) and import
  // (a single mutating request) can take a moment for a large library — warn
  // before an accidental refresh/navigation loses the in-flight result. The
  // import itself is safe either way (the backend writes it in one DB
  // transaction, so a dropped connection mid-request can't leave a partial
  // import — this is purely about not losing visibility into the outcome).
  useEffect(() => {
    if (!exportLoading && !importFields.isPending) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [exportLoading, importFields.isPending])

  function resetImport() {
    setImportParsed(null)
    setImportParseErrors([])
    setImportParseWarnings([])
    setImportServerErrors([])
    setImportSuccess(null)
    if (importFileRef.current) importFileRef.current.value = ''
  }

  async function handleImportFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setImportParseErrors([])
    setImportParseWarnings([])
    setImportServerErrors([])
    setImportSuccess(null)
    const buf = await file.arrayBuffer()
    // Known form handles let the parser validate cross-form refs
    // ({formhandle.fieldId}) up front. Failure to load them is non-fatal — the
    // backend import re-validates and rejects unknown refs with row errors.
    let knownForms: KnownFormForImport[] | undefined
    try {
      const { items } = await formsApi.listDefinitions()
      const byHandle = new Map<string, KnownFormForImport>()
      for (const def of [...items].sort((a, b) => (b.version ?? 0) - (a.version ?? 0))) {
        if (!def.handle || byHandle.has(def.handle)) continue
        byHandle.set(def.handle, {
          handle: def.handle,
          name: def.name,
          fieldIds: new Set(allFields(def).map(f => f.id)),
        })
      }
      knownForms = [...byHandle.values()]
    } catch {
      knownForms = undefined
    }
    const result = await parseLibraryFieldWorkbook(buf, knownForms)
    if (!result.ok) {
      setImportParsed(null)
      setImportParseErrors(result.errors)
    } else {
      setImportParsed(result.rows)
      setImportParseWarnings(result.warnings)
    }
  }

  function handleImportSubmit() {
    if (!importParsed || importParsed.length === 0) return
    setImportServerErrors([])
    importFields.mutate(importParsed, {
      onSuccess: ({ created, updated }) => {
        setImportSuccess({ created, updated })
        setImportParsed(null)
        if (importFileRef.current) importFileRef.current.value = ''
      },
      onError: e => {
        // Backend returns { errors: string[] } inside the error message JSON.
        let msgs: string[] = []
        try {
          const parsed = JSON.parse(e.message) as { errors?: string[] }
          msgs = parsed.errors ?? [e.message]
        } catch {
          msgs = [e.message]
        }
        setImportServerErrors(msgs)
      },
    })
  }

  async function handleDownloadTemplate() {
    setImportTemplateLoading(true)
    try {
      const buf = await buildLibraryFieldImportTemplate(categories)
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'library-field-import-template.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setImportTemplateLoading(false)
    }
  }

  async function handleExportFields() {
    setExportLoading(true)
    setExportError(null)
    setExportNotices([])
    try {
      // listAllFields pages past the 100-row API cap — the export must cover
      // every field, not just the current page/filter of the table below.
      const allFields = await fieldLibraryApi.listAllFields()
      const { buf, skipped } = await buildLibraryFieldExport(allFields, categories)
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'library-fields-export.xlsx'
      a.click()
      URL.revokeObjectURL(url)

      const notices: string[] = []
      if (skipped.length > 0) {
        notices.push(
          `${skipped.length} field${skipped.length === 1 ? '' : 's'} skipped — the import format doesn't ` +
            `support their type: ${skipped.map(s => `${s.label} (${s.fieldType})`).join(', ')}.`,
        )
      }
      setExportNotices(notices)
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Could not export fields.')
    } finally {
      setExportLoading(false)
    }
  }

  if (!canRead) {
    return (
      <div className='w-full px-6 py-8'>
        <div className='flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800'>
          <AlertCircle className='h-4 w-4 shrink-0' />
          You do not have permission to view the field library.
        </div>
      </div>
    )
  }

  function handleCreate(payload: CreateLibraryFieldPayload) {
    setCreateServerError(null)
    create.mutate(payload, {
      onSuccess: () => setCreateOpen(false),
      onError: e =>
        setCreateServerError(e instanceof Error ? e.message : 'Could not create field.'),
    })
  }

  function handleUpdate(id: string, payload: CreateLibraryFieldPayload) {
    setEditServerError(null)
    update.mutate(
      { id, payload },
      {
        onSuccess: () => setEditing(null),
        onError: e =>
          setEditServerError(e instanceof Error ? e.message : 'Could not update field.'),
      },
    )
  }

  function handleDelete(id: string) {
    setDeleteServerError(null)
    remove.mutate(id, {
      onSuccess: () => setDeleting(null),
      onError: e =>
        setDeleteServerError(e instanceof Error ? e.message : 'Could not delete field.'),
    })
  }

  // Row-level Enabled switch — optimistic (flips instantly in the cache; every
  // other action in this file waits on the network, but a toggle needs to feel
  // immediate). Reverts automatically on error via toggleEnabled's onError.
  function handleToggleEnabled(field: FieldDefinition, next: boolean) {
    setEnabledToggleErrors(prev => {
      const { [field.id]: _omit, ...rest } = prev
      return rest
    })
    toggleEnabled.mutate(
      { id: field.id, enabled: next },
      {
        onError: e =>
          setEnabledToggleErrors(prev => ({
            ...prev,
            [field.id]: e instanceof Error ? e.message : 'Could not update field.',
          })),
      },
    )
  }

  // Facet chip base class — the active variant is inlined per chip
  const chipBase =
    'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors'
  const chipActive = 'border-transparent bg-primary text-primary-foreground'
  const chipIdle = 'border-border bg-background text-muted-foreground hover:bg-muted'

  return (
    <div className='w-full px-6 py-8'>
      {/* Header */}
      <div className='mb-6 flex items-center justify-between gap-3'>
        <PageHeading title='Field Library' description='A reusable catalog of fields. Insert them onto forms in the builder — each copy is independent (copy by value).' />
        <div className='flex items-center gap-2'>
          {canManageCategories && (
            <Button variant='outline' onClick={() => setManageCategoriesOpen(true)}>
              <Tags className='mr-2 h-4 w-4' />
              Manage Categories
            </Button>
          )}
          {canCreate && (
            <Button variant='outline' onClick={() => { resetImport(); setImportOpen(true) }}>
              <Upload className='mr-2 h-4 w-4' />
              Import
            </Button>
          )}
          <div className='flex flex-col items-end gap-1'>
            <Button variant='outline' disabled={exportLoading} onClick={handleExportFields}>
              {exportLoading
                ? <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                : <Download className='mr-2 h-4 w-4' />}
              Export fields (.xlsx)
            </Button>
            {exportError && (
              <p className='max-w-xs text-right text-xs text-destructive'>{exportError}</p>
            )}
            {exportNotices.map((n, i) => (
              <p key={i} className='max-w-xs text-right text-xs text-amber-700'>{n}</p>
            ))}
          </div>
          {canCreate && (
            <Button
              onClick={() => {
                setCreateServerError(null)
                setCreateOpen(true)
              }}
            >
              <Plus className='mr-2 h-4 w-4' />
              New Field
            </Button>
          )}
        </div>
      </div>

      {/* Search + type-filter facets */}
      <div className='mb-4 space-y-3'>
        <div className='flex flex-wrap items-start gap-2'>
          <Input
            placeholder='Search fields by label or help text…'
            value={q}
            onChange={e => setQ(e.target.value)}
            className='max-w-sm'
          />
          {canReadCategories && categories.length > 0 && (
            <SingleSelect
              className='w-56'
              ariaLabel='Filter by category'
              placeholder='All categories'
              resetLabel='All categories'
              searchPlaceholder='Search categories…'
              value={categoryFilter}
              onChange={v => {
                setCategoryFilter(v)
                setSkip(0)
              }}
              options={categories.map(c => ({ id: c.id, label: c.name, count: c.fieldCount }))}
            />
          )}
          <label className='flex items-center gap-2 rounded-md border px-3 py-2 text-sm'>
            <Switch
              checked={showDisabled}
              onCheckedChange={v => {
                setShowDisabled(v)
                setSkip(0)
              }}
            />
            <span className='text-muted-foreground'>Show disabled</span>
          </label>
        </div>

        {/* Only render chips once we have data so counts are meaningful */}
        {list.data && (
          <div className='flex flex-wrap gap-1.5'>
            <button
              type='button'
              onClick={() => {
                setFieldTypeFilter('')
                setSkip(0)
              }}
              className={`${chipBase} ${fieldTypeFilter === '' ? chipActive : chipIdle}`}
            >
              All ({allCount})
            </button>
            {FIELD_TYPE_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type='button'
                onClick={() => {
                  setFieldTypeFilter(value)
                  setSkip(0)
                }}
                className={`${chipBase} ${fieldTypeFilter === value ? chipActive : chipIdle}`}
              >
                {label} ({typeCounts[value] ?? 0})
              </button>
            ))}
          </div>
        )}
      </div>

      <Card>
        <CardContent className='p-0'>
          {/* Initial load spinner (no placeholder data yet) */}
          {list.isLoading && (
            <div className='flex items-center justify-center py-20'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          )}

          {list.isError && (
            <div className='flex items-center gap-2 px-4 py-6 text-sm text-destructive'>
              <AlertCircle className='h-4 w-4 shrink-0' />
              Could not load fields. {(list.error as Error).message}
            </div>
          )}

          {/* While a filter/toggle change is refetching, items can read 0 before the
             real result lands (keepPreviousData doesn't cover every transition here) —
             show a loader instead of prematurely claiming there are no results. */}
          {list.data && items.length === 0 && list.isFetching && (
            <div className='flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Loading…
            </div>
          )}

          {list.data && items.length === 0 && !list.isFetching && (
            <div className='py-16 text-center text-sm text-muted-foreground'>
              {debouncedQ || fieldTypeFilter || skip > 0
                ? 'No fields match your search.'
                : !showDisabled
                  ? 'No enabled fields in the library. Turn on "Show disabled" to see disabled fields, or create one to get started.'
                  : `No fields in the library yet.${canCreate ? ' Create one to get started.' : ''}`}
            </div>
          )}

          {list.data && items.length > 0 && (
            <>
            <Table>
              {/* Header and body are both driven by `cols`, so a hidden column
                 disappears from both at once. Every head is rendered from the
                 same declared list to keep the two from shearing apart as
                 columns are added. */}
              <TableHeader>
                <TableRow>
                  {cols.visible.map(({ def }) => (
                    <ResizableTableHead
                      key={def.id}
                      columnId={def.id}
                      columns={cols}
                      label={def.header}
                      className={def.id === 'actions' ? 'text-right' : undefined}
                      // The actions cell holds a fixed row of icon buttons, so
                      // there is nothing to gain by resizing it and a narrow
                      // drag would just clip them.
                      resizable={def.id !== 'actions'}
                    >
                      {def.header}
                    </ResizableTableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(field => {
                  const disabled = field.enabled === false
                  const dim = disabled ? 'opacity-60' : ''
                  return (
                  // Muted look combines a row-wide background wash (bg-muted, via
                  // plain background-color — doesn't affect descendant rendering)
                  // with per-cell opacity-60 + text-muted-foreground below. The
                  // background wash is what actually reads as "muted" at a glance;
                  // opacity alone on a white row was too subtle to notice.
                  <TableRow key={field.id} className={disabled ? 'bg-muted/40' : undefined}>
                    <ColumnCells
                      columns={cols}
                      cells={{
                        label: (
                          <TableCell
                            className={`font-medium ${disabled ? 'text-muted-foreground' : ''} ${dim}`}
                          >
                            {field.label}
                          </TableCell>
                        ),
                        // Monospace because it is still an identifier — just
                        // shown in its readable PascalCase form rather than as
                        // the stored slug, which is on hover. Definition and
                        // Example are clamped to two lines with the full text on
                        // hover, so a long one cannot stretch a row past the
                        // rest of the table.
                        // Monospace for the same reason as System name: it is
                        // an identifier, and it is shown verbatim because a
                        // formula has to be typed to match it character for
                        // character.
                        refName: (
                          <TableCell className={`font-mono text-xs ${dim}`}>
                            {field.refName ? (
                              <span title={`Reference this field in a formula as {${field.refName}}`}>
                                {field.refName}
                              </span>
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                        ),
                        systemName: (
                          <TableCell className={`font-mono text-xs ${dim}`}>
                            <SystemNameCell field={field} />
                          </TableCell>
                        ),
                        definition: (
                          <TableCell className={`text-muted-foreground ${dim}`}>
                            {field.definition ? (
                              <span className='line-clamp-2 block text-xs' title={field.definition}>
                                {field.definition}
                              </span>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        ),
                        example: (
                          <TableCell className={`text-muted-foreground ${dim}`}>
                            {field.example ? (
                              <span className='line-clamp-2 block text-xs' title={field.example}>
                                {field.example}
                              </span>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        ),
                        fieldType: (
                          <TableCell className={dim}>
                            {field.fieldType === 'process' &&
                            configuredProcessFieldIds.has(field.id) ? (
                              <Badge
                                variant='outline'
                                className='gap-1 border-green-300 bg-green-50 text-xs text-green-700'
                                title='This process field has configured steps'
                              >
                                <span className='h-1.5 w-1.5 rounded-full bg-green-500' />
                                Process
                              </Badge>
                            ) : (
                              <Badge variant='outline' className='text-xs'>
                                {FIELD_TYPE_LABELS[field.fieldType]}
                              </Badge>
                            )}
                          </TableCell>
                        ),
                        required: (
                          <TableCell className={`text-muted-foreground ${dim}`}>
                            {field.required ? 'Required' : 'Optional'}
                          </TableCell>
                        ),
                        flags: (
                          <TableCell className={dim}>
                            <div className='flex flex-wrap items-center gap-1'>
                              {field.isGate && (
                                <Badge variant='secondary' className='gap-1 text-xs'>
                                  <Zap className='h-3 w-3' /> Gate
                                </Badge>
                              )}
                              {field.anchored && (
                                <Badge variant='secondary' className='gap-1 text-xs'>
                                  <Anchor className='h-3 w-3' /> Anchored
                                </Badge>
                              )}
                              {field.isMrField && (
                                <Badge variant='secondary' className='gap-1 text-xs'>
                                  <ClipboardList className='h-3 w-3' /> MR
                                  {field.mrFieldKey ? ` · ${MR_FIELD_LABELS[field.mrFieldKey]}` : ''}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                        ),
                        // No opacity-60/text-muted-foreground here, unlike the
                        // other cells: CSS opacity compounds with ancestors, so
                        // the switch needs to sit outside that dimming to stay
                        // fully opaque and obviously operable. The row's
                        // bg-muted wash still shows through behind it.
                        enabled: (
                          <TableCell>
                            <Switch
                              checked={field.enabled ?? true}
                              disabled={!canEdit}
                              onCheckedChange={next => handleToggleEnabled(field, next)}
                            />
                            {enabledToggleErrors[field.id] && (
                              <p className='mt-1 text-xs text-destructive'>
                                {enabledToggleErrors[field.id]}
                              </p>
                            )}
                          </TableCell>
                        ),
                        categories: (
                          <TableCell className={dim}>
                            <div className='flex flex-wrap items-center gap-1'>
                              {(field.categoryIds ?? []).length === 0 ? (
                                <span className='text-xs text-muted-foreground'>—</span>
                              ) : (
                                (field.categoryIds ?? []).map(cid => (
                                  <Badge
                                    key={cid}
                                    variant='secondary'
                                    className='max-w-[9rem] gap-1 font-normal'
                                  >
                                    <Tags className='h-3 w-3 shrink-0 opacity-60' />
                                    <span className='truncate'>
                                      {categoryNameById.get(cid) ?? 'Unknown'}
                                    </span>
                                  </Badge>
                                ))
                              )}
                            </div>
                          </TableCell>
                        ),
                        actions: (
                          <TableCell className={dim}>
                            <div className='flex items-center justify-end gap-1'>
                              {field.fieldType === 'process' && (
                                <Button variant='ghost' size='icon' title='Configure process' asChild>
                                  <Link
                                    to={`/form-builder?tab=process&step=equipment&field=${field.id}`}
                                  >
                                    <Workflow className='h-4 w-4' />
                                  </Link>
                                </Button>
                              )}
                              <Button
                                variant='ghost'
                                size='icon'
                                title='Used by forms'
                                onClick={() => setUsageFor(field)}
                              >
                                <Link2 className='h-4 w-4' />
                              </Button>
                              {canEdit && (
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  title='Edit field'
                                  onClick={() => {
                                    setEditServerError(null)
                                    setEditing(field)
                                  }}
                                >
                                  <Pencil className='h-4 w-4' />
                                </Button>
                              )}
                              {canDelete && (
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  title='Delete field'
                                  onClick={() => {
                                    setDeleteServerError(null)
                                    setDeleting(field)
                                  }}
                                >
                                  <Trash2 className='h-4 w-4 text-destructive' />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        ),
                      }}
                    />
                  </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            </>
          )}

          {/* Pagination bar — rendered whenever total > 0, regardless of current page's item count */}
          {list.data && total > 0 && (
            <div className='flex items-center justify-between border-t px-4 py-3 text-sm text-muted-foreground'>
              <span className='flex items-center gap-2'>
                {list.isFetching && (
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                )}
                {items.length > 0
                  ? `Showing ${skip + 1}–${skip + items.length} of ${total}`
                  : `0 of ${total}`}
              </span>
              <div className='flex items-center gap-2'>
                <select
                  value={take}
                  onChange={e => setTake(Number(e.target.value))}
                  className='h-8 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring'
                >
                  {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} / page</option>)}
                </select>
                <div className='flex items-center gap-1.5'>
                  <Button
                    size='sm'
                    variant='outline'
                    disabled={skip === 0}
                    onClick={() => setSkip(s => Math.max(0, s - take))}
                  >
                    <ChevronLeft className='mr-1 h-4 w-4' />
                    Prev
                  </Button>
                  <Button
                    size='sm'
                    variant='outline'
                    disabled={skip + items.length >= total}
                    onClick={() => setSkip(s => s + take)}
                  >
                    Next
                    <ChevronRight className='ml-1 h-4 w-4' />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}

      {createOpen && (
        <FieldFormDialog
          pending={create.isPending}
          serverError={createServerError}
          categories={categories}
          canReadCategories={canReadCategories}
          canManageCategories={canManageCategories}
          onClose={() => {
            setCreateServerError(null)
            setCreateOpen(false)
          }}
          onSubmit={handleCreate}
        />
      )}

      {editing && (
        <FieldFormDialog
          field={editing}
          pending={update.isPending}
          serverError={editServerError}
          categories={categories}
          canReadCategories={canReadCategories}
          canManageCategories={canManageCategories}
          onClose={() => {
            setEditServerError(null)
            setEditing(null)
          }}
          onSubmit={payload => handleUpdate(editing.id, payload)}
        />
      )}

      {deleting && (
        <DeleteFieldDialog
          field={deleting}
          pending={remove.isPending}
          serverError={deleteServerError}
          onClose={() => {
            setDeleteServerError(null)
            setDeleting(null)
          }}
          onConfirm={() => handleDelete(deleting.id)}
        />
      )}

      {usageFor && <UsageDialog field={usageFor} onClose={() => setUsageFor(null)} />}

      {manageCategoriesOpen && (
        <ManageCategoriesDialog onClose={() => setManageCategoriesOpen(false)} />
      )}

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={open => { if (!open) resetImport(); setImportOpen(open) }}>
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle>Import library fields</DialogTitle>
            <DialogDescription>
              Download the template, fill in your fields, then upload the completed file. All rows are
              validated before anything is written — one bad row rejects the whole file.
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4 py-2'>
            <Button
              variant='outline'
              size='sm'
              disabled={importTemplateLoading}
              onClick={handleDownloadTemplate}
            >
              {importTemplateLoading
                ? <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                : <Download className='mr-2 h-4 w-4' />}
              Download template (.xlsx)
            </Button>

            <div>
              <Label htmlFor='import-file' className='mb-1 block text-sm'>
                Upload completed file
              </Label>
              <input
                id='import-file'
                ref={importFileRef}
                type='file'
                accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                className='block w-full text-sm text-muted-foreground file:mr-3 file:rounded file:border file:border-border file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium'
                onChange={handleImportFileChange}
              />
            </div>

            {importParseErrors.length > 0 && (
              <div className='rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm'>
                <p className='mb-1 font-medium text-destructive'>Fix these errors before importing:</p>
                <ul className='space-y-0.5 text-destructive'>
                  {importParseErrors.map((e, i) => <li key={i}>• {e}</li>)}
                </ul>
              </div>
            )}

            {importServerErrors.length > 0 && (
              <div className='rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm'>
                <p className='mb-1 font-medium text-destructive'>Import rejected by the server:</p>
                <ul className='space-y-0.5 text-destructive'>
                  {importServerErrors.map((e, i) => <li key={i}>• {e}</li>)}
                </ul>
              </div>
            )}

            {importParseWarnings.length > 0 && (
              <div className='rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800'>
                {importParseWarnings.map((w, i) => <p key={i}>{w}</p>)}
              </div>
            )}

            {importSuccess !== null && (
              <div className='flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800'>
                <CheckCircle2 className='h-4 w-4 shrink-0' />
                Import complete — {importSuccess.created} field{importSuccess.created === 1 ? '' : 's'} created, {importSuccess.updated} updated.
              </div>
            )}

            {importParsed && importParsed.length > 0 && importSuccess === null && (
              <p className='text-sm text-muted-foreground'>
                {importParsed.length} row{importParsed.length === 1 ? '' : 's'} ready — existing fields with the
                same label are updated; new categories are created automatically.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={() => { resetImport(); setImportOpen(false) }}>
              {importSuccess !== null ? 'Close' : 'Cancel'}
            </Button>
            {importSuccess === null && (
              <Button
                disabled={!importParsed || importParsed.length === 0 || importFields.isPending}
                onClick={handleImportSubmit}
              >
                {importFields.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                Import {importParsed ? `${importParsed.length} field${importParsed.length === 1 ? '' : 's'}` : ''}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Create / Edit dialog ─────────────────────────────────────────────────────

function FieldFormDialog({
  field,
  pending,
  serverError,
  categories,
  canReadCategories,
  canManageCategories,
  onClose,
  onSubmit,
}: {
  field?: FieldDefinition
  pending: boolean
  serverError?: string | null
  categories: FieldCategory[]
  canReadCategories: boolean
  canManageCategories: boolean
  onClose: () => void
  onSubmit: (payload: CreateLibraryFieldPayload) => void
}) {
  const [draft, setDraft] = useState<FieldDraft>(() =>
    field ? draftFromField(field) : emptyDraft(),
  )
  const [formError, setFormError] = useState<string | null>(null)

  // Inline "create category" affordance.
  // enabled=canReadCategories suppresses the list query for users who lack read permission.
  const { create: createCategory } = useFieldCategories({ take: CATEGORY_TAKE }, canReadCategories)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [categoryError, setCategoryError] = useState<string | null>(null)

  const patch = (p: Partial<FieldDraft>) => setDraft(prev => ({ ...prev, ...p }))

  // Selected categories, ordered as stored on the draft, for the removable chips.
  const selectedCategories = draft.categoryIds
    .map(id => categories.find(c => c.id === id))
    .filter((c): c is FieldCategory => c != null)

  async function handleCreateCategory() {
    const name = newCategoryName.trim()
    if (!name) return
    setCategoryError(null)
    try {
      const created = await createCategory.mutateAsync({ name })
      patch({ categoryIds: [...draft.categoryIds, created.id] })
      setNewCategoryName('')
    } catch (e) {
      setCategoryError(e instanceof Error ? e.message : 'Could not create category.')
    }
  }

  const isCalculated = draft.fieldType === 'calculated'
  const isDropdown = draft.fieldType === 'dropdown'
  const isSignature = draft.fieldType === 'signature'
  const isMrEligible = isMrEligibleType(draft.fieldType)
  const isMr = isMrEligible && draft.isMrField

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!draft.label.trim()) {
      setFormError('Label is required.')
      return
    }
    if (isCalculated) {
      const refs = draft.formula.trim() ? extractFormulaRefs(draft.formula) : []
      if (!draft.formula.trim() || refs.length === 0) {
        setFormError('A calculated field needs a formula that references at least one field.')
        return
      }
    }
    if (isDropdown && draft.options.map(o => o.trim()).filter(Boolean).length === 0) {
      setFormError('A dropdown needs at least one option.')
      return
    }
    if (isMr && !draft.mrFieldKey) {
      setFormError('Select which medical-record attribute this MR field maps to.')
      return
    }
    setFormError(null)
    onSubmit(draftToPayload(draft))
  }

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>{field ? 'Edit field' : 'New field'}</DialogTitle>
          <DialogDescription>
            Define a reusable field for the library. It can later be inserted onto any form.
          </DialogDescription>
        </DialogHeader>

        {field && (
          <div className='mb-1'>
            <UsageSummary fieldId={field.id} />
          </div>
        )}

        <form onSubmit={submit} className='flex flex-col gap-4'>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='field-label'>Label</Label>
            <Input
              id='field-label'
              value={draft.label}
              autoFocus
              onChange={e => patch({ label: e.target.value })}
              aria-describedby='field-label-hint'
            />
            {/* Stated up front, because the rule is not guessable and the
                alternative is learning it from a rejected save (z8nrz7cr9r).
                The name is how an author picks a definition out of a list, so a
                second field under the same name makes that list unusable — and
                for a Process, whose configuration is keyed on the library row,
                picking the wrong one configures another form's materials.

                Deliberately not validated live here: this dialog is not given
                the field list, and the page's is filtered and paginated, so a
                client-side check would clear a name that is actually taken on
                another page. The server owns the answer and its 409 renders
                through `serverError` below. */}
            <p id='field-label-hint' className='text-xs text-muted-foreground'>
              Must be unique — a name already used by another field in the library
              cannot be reused.
            </p>
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label>Field type</Label>
            <Select
              value={draft.fieldType}
              onValueChange={v => patch({ fieldType: v as CgtFieldType })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_TYPE_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='field-help'>Help text</Label>
            <Textarea
              id='field-help'
              rows={2}
              value={draft.helpText}
              onChange={e => patch({ helpText: e.target.value })}
              className='resize-none'
            />
          </div>

          {/* System name — shown, never edited. It is the field's stable machine
              identifier, assigned by us in the database, so there is no input to
              render: the API does not accept it and a disabled box that looked
              like the others would only invite the question. Only on an existing
              field: a new one has no label-derived name worth showing before the
              label itself is typed. */}
          {field && <SystemNameField field={field} />}

          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='field-definition'>Definition</Label>
            <Textarea
              id='field-definition'
              rows={3}
              value={draft.definition}
              onChange={e => patch({ definition: e.target.value })}
              aria-describedby='field-definition-hint'
            />
            <p id='field-definition-hint' className='text-xs text-muted-foreground'>
              What this field means. Reference documentation for whoever fills the
              form — unlike help text, it is not shown on the form itself.
            </p>
          </div>

          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='field-example'>Example</Label>
            <Input
              id='field-example'
              value={draft.example}
              onChange={e => patch({ example: e.target.value })}
              placeholder='e.g. 4.2'
            />
          </div>

          {!isCalculated && !isMr && (
            <label className='flex items-center justify-between rounded-lg border px-3 py-2'>
              <span className='text-sm font-medium'>Required</span>
              <Switch
                checked={draft.required}
                onCheckedChange={c => patch({ required: c })}
              />
            </label>
          )}

          {/* Attachment — the same three-value choice the canvas PropertiesPanel
              offers, so a field configured here and one configured on the canvas
              are described identically. */}
          {supportsAttachment(draft.fieldType) && (
            <div className='space-y-1.5'>
              <Label htmlFor='library-attachment-mode'>Attachment</Label>
              <Select
                value={attachmentMode(
                  draft.attachment
                    ? { enabled: true, required: draft.attachmentRequired }
                    : undefined,
                )}
                onValueChange={(m: AttachmentMode) =>
                  patch({ attachment: m !== 'none', attachmentRequired: m === 'required' })
                }
              >
                <SelectTrigger id='library-attachment-mode'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ATTACHMENT_MODES.map(m => (
                    <SelectItem key={m} value={m}>
                      {ATTACHMENT_MODE_LABELS[m]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className='text-xs text-muted-foreground'>
                JPEG, PNG or PDF. Required blocks form completion until a file is attached.
              </p>
            </div>
          )}

          {isMrEligible && (
            <div className='flex flex-col gap-2'>
              <label className='flex items-center justify-between rounded-lg border px-3 py-2'>
                <span>
                  <span className='block text-sm font-medium'>MR Field (Medical Record)</span>
                  <span className='block text-xs text-muted-foreground'>
                    Auto-populate read-only from the patient record
                  </span>
                </span>
                <Switch
                  checked={draft.isMrField}
                  onCheckedChange={c => patch({ isMrField: c })}
                />
              </label>
              {isMr && (
                <div className='flex flex-col gap-1.5'>
                  <Label>Patient attribute</Label>
                  <Select
                    value={draft.mrFieldKey || undefined}
                    onValueChange={v => patch({ mrFieldKey: v as MrFieldKey })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select attribute…' />
                    </SelectTrigger>
                    <SelectContent>
                      {MR_FIELD_KEYS.map(key => (
                        <SelectItem key={key} value={key}>
                          {MR_FIELD_LABELS[key]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {isDropdown && (
            <div className='flex flex-col gap-2'>
              <Label>Options</Label>
              {draft.options.map((opt, i) => (
                <div key={i} className='flex items-center gap-2'>
                  <Input
                    value={opt}
                    onChange={e => {
                      const next = [...draft.options]
                      next[i] = e.target.value
                      patch({ options: next })
                    }}
                  />
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    onClick={() => patch({ options: draft.options.filter((_, j) => j !== i) })}
                  >
                    <X className='h-4 w-4' />
                  </Button>
                </div>
              ))}
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='self-start'
                onClick={() => patch({ options: [...draft.options, ''] })}
              >
                <Plus className='mr-1.5 h-3.5 w-3.5' /> Add option
              </Button>
            </div>
          )}

          {isCalculated && (
            <LibraryFormulaBuilder
              formula={draft.formula}
              displayFormat={draft.displayFormat as DisplayFormat}
              onChange={changes => {
                if (changes.formula !== undefined) patch({ formula: changes.formula })
                if (changes.displayFormat !== undefined) patch({ displayFormat: changes.displayFormat })
              }}
            />
          )}

          {isSignature && (
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='field-group'>Required group</Label>
              <Input
                id='field-group'
                value={draft.requiredGroup}
                placeholder='e.g. MedicalDirector'
                onChange={e => patch({ requiredGroup: e.target.value })}
              />
            </div>
          )}

          <label className='flex items-center justify-between rounded-lg border px-3 py-2'>
            <span>
              <span className='block text-sm font-medium'>Anchored</span>
              <span className='block text-xs text-muted-foreground'>
                Surface in the procedure header across forms
              </span>
            </span>
            <Switch checked={draft.anchored} onCheckedChange={c => patch({ anchored: c })} />
          </label>

          {supportsWriteOnce({ fieldType: draft.fieldType, isMrField: isMr }) && (
            <label className='flex items-center justify-between rounded-lg border px-3 py-2'>
              <span>
                <span className='block text-sm font-medium'>Can change</span>
                <span className='block text-xs text-muted-foreground'>
                  {draft.canChange
                    ? 'Stays editable after it is filled in'
                    : 'Locks permanently once it has a value'}
                </span>
              </span>
              <Switch checked={draft.canChange} onCheckedChange={c => patch({ canChange: c })} />
            </label>
          )}

          <label className='flex items-center justify-between rounded-lg border px-3 py-2'>
            <span>
              <span className='block text-sm font-medium'>Enabled</span>
              <span className='block text-xs text-muted-foreground'>
                Offered when authors insert fields onto a form
              </span>
            </span>
            <Switch checked={draft.enabled} onCheckedChange={c => patch({ enabled: c })} />
          </label>

          {canReadCategories && (
            <div className='flex flex-col gap-1.5'>
              <Label className='flex items-center gap-1.5'>
                <Tags className='h-3.5 w-3.5 text-muted-foreground' />
                Categories
              </Label>
              <p className='text-xs text-muted-foreground'>
                Group this field so it can be inserted with others in the builder.
              </p>
              <MultiSelect
                options={categories.map(c => ({ id: c.id, label: c.name }))}
                selected={draft.categoryIds}
                onChange={ids => patch({ categoryIds: ids })}
                placeholder='No categories'
              />

              {selectedCategories.length > 0 && (
                <div className='flex flex-wrap gap-1'>
                  {selectedCategories.map(c => (
                    <Badge
                      key={c.id}
                      variant='secondary'
                      className='max-w-[12rem] gap-1 pr-1 font-normal'
                    >
                      <span className='truncate'>{c.name}</span>
                      <button
                        type='button'
                        aria-label={`Remove ${c.name}`}
                        onClick={() =>
                          patch({ categoryIds: draft.categoryIds.filter(id => id !== c.id) })
                        }
                        className='rounded-full p-0.5 text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring'
                      >
                        <X className='h-3 w-3' />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {canManageCategories && (
                <div className='mt-1 flex items-center gap-2'>
                  <Input
                    value={newCategoryName}
                    placeholder='Create a new category…'
                    onChange={e => setNewCategoryName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        void handleCreateCategory()
                      }
                    }}
                    className='h-8'
                  />
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    className='shrink-0'
                    disabled={!newCategoryName.trim() || createCategory.isPending}
                    onClick={() => void handleCreateCategory()}
                  >
                    <Plus className='mr-1 h-3.5 w-3.5' /> Create category
                  </Button>
                </div>
              )}
              {categoryError && <p className='text-xs text-destructive'>{categoryError}</p>}
            </div>
          )}

          {formError && <p className='text-sm text-destructive'>{formError}</p>}
          {serverError && <p className='text-sm text-destructive'>{serverError}</p>}

          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={pending || !draft.label.trim()}>
              {pending ? 'Saving…' : field ? 'Save changes' : 'Create field'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ─── Delete dialog ────────────────────────────────────────────────────────────

function DeleteFieldDialog({
  field,
  pending,
  serverError,
  onClose,
  onConfirm,
}: {
  field: FieldDefinition
  pending: boolean
  serverError?: string | null
  onClose: () => void
  onConfirm: () => void
}) {
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Delete "{field.label}"?</DialogTitle>
          <DialogDescription>
            This removes the field from the library. Forms that already use it keep their own copy.
          </DialogDescription>
        </DialogHeader>
        <UsageSummary fieldId={field.id} />
        {serverError && <p className='text-sm text-destructive'>{serverError}</p>}
        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type='button' variant='destructive' onClick={onConfirm} disabled={pending}>
            {pending ? 'Deleting…' : 'Delete field'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Usage / association dialog ───────────────────────────────────────────────

function UsageDialog({ field, onClose }: { field: FieldDefinition; onClose: () => void }) {
  const usage = useFieldUsage(field.id)
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Forms using "{field.label}"</DialogTitle>
          <DialogDescription>
            Forms that have copied this library field. Each keeps an independent copy.
          </DialogDescription>
        </DialogHeader>
        {usage.isLoading && <p className='text-sm text-muted-foreground'>Loading…</p>}
        {usage.isError && (
          <p className='text-sm text-destructive'>
            Could not load usage. {(usage.error as Error).message}
          </p>
        )}
        {usage.data && usage.data.count === 0 && (
          <p className='text-sm text-muted-foreground'>No forms use this field yet.</p>
        )}
        {usage.data && usage.data.count > 0 && (
          <ul className='divide-y rounded-md border'>
            {usage.data.forms.map(f => (
              <li key={f.id} className='flex items-center justify-between px-3 py-2 text-sm'>
                <span className='font-medium'>{f.name}</span>
                <span className='flex items-center gap-2 text-xs text-muted-foreground'>
                  <Badge variant='outline' className='text-xs'>
                    {f.category}
                  </Badge>
                  v{f.version}
                </span>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Manage categories dialog ─────────────────────────────────────────────────

function ManageCategoriesDialog({ onClose }: { onClose: () => void }) {
  const { list, create, update, remove } = useFieldCategories({ take: CATEGORY_TAKE })
  const allItems = list.data?.items ?? []

  const [newName, setNewName] = useState('')
  const [search, setSearch] = useState('')
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)
  const [deleting, setDeleting] = useState<FieldCategory | null>(null)
  const [error, setError] = useState<string | null>(null)

  const query = search.trim().toLowerCase()
  const items = query
    ? allItems.filter(c => c.name.toLowerCase().includes(query))
    : allItems

  function handleCreate() {
    const name = newName.trim()
    if (!name) return
    setError(null)
    create.mutate(
      { name },
      {
        onSuccess: () => setNewName(''),
        onError: e => setError(e instanceof Error ? e.message : 'Could not create category.'),
      },
    )
  }

  function handleRename() {
    if (!renaming) return
    const name = renaming.name.trim()
    if (!name) return
    setError(null)
    update.mutate(
      { id: renaming.id, payload: { name } },
      {
        onSuccess: () => setRenaming(null),
        onError: e => setError(e instanceof Error ? e.message : 'Could not rename category.'),
      },
    )
  }

  function handleDelete() {
    if (!deleting) return
    setError(null)
    remove.mutate(deleting.id, {
      onSuccess: () => setDeleting(null),
      onError: e => setError(e instanceof Error ? e.message : 'Could not delete category.'),
    })
  }

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Manage categories</DialogTitle>
          <DialogDescription>
            Group reusable library fields. Deleting a category only removes the grouping — the
            fields themselves are untouched.
          </DialogDescription>
        </DialogHeader>

        {/* Create */}
        <div className='flex items-center gap-2'>
          <Input
            value={newName}
            placeholder='New category name…'
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleCreate()
              }
            }}
          />
          <Button
            type='button'
            disabled={!newName.trim() || create.isPending}
            onClick={handleCreate}
          >
            <Plus className='mr-1.5 h-4 w-4' /> Add
          </Button>
        </div>

        {error && <p className='text-sm text-destructive'>{error}</p>}

        {/* Search — only worth showing once the list is long enough to scan. */}
        {allItems.length > 6 && (
          <div className='relative'>
            <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
            <Input
              value={search}
              placeholder='Search categories…'
              onChange={e => setSearch(e.target.value)}
              className='h-9 pl-9'
            />
          </div>
        )}

        {/* List */}
        {list.isLoading ? (
          <div className='flex items-center justify-center py-8'>
            <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
          </div>
        ) : allItems.length === 0 ? (
          <p className='py-6 text-center text-sm text-muted-foreground'>
            No categories yet — create one to group related fields.
          </p>
        ) : items.length === 0 ? (
          <p className='py-6 text-center text-sm text-muted-foreground'>
            No categories match “{search.trim()}”.
          </p>
        ) : (
          <ul className='divide-y rounded-md border'>
            {items.map(c => (
              <li key={c.id} className='flex items-center justify-between gap-2 px-3 py-2'>
                {renaming?.id === c.id ? (
                  <div className='flex flex-1 items-center gap-2'>
                    <Input
                      value={renaming.name}
                      autoFocus
                      onChange={e => setRenaming({ id: c.id, name: e.target.value })}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleRename()
                        }
                      }}
                      className='h-8'
                    />
                    <Button
                      type='button'
                      size='sm'
                      disabled={!renaming.name.trim() || update.isPending}
                      onClick={handleRename}
                    >
                      Save
                    </Button>
                    <Button
                      type='button'
                      size='sm'
                      variant='ghost'
                      onClick={() => setRenaming(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className='min-w-0'>
                      <p className='truncate text-sm font-medium'>{c.name}</p>
                      <p className='text-xs text-muted-foreground'>
                        {c.fieldCount} field{c.fieldCount === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className='flex shrink-0 items-center gap-1'>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        title='Rename category'
                        onClick={() => setRenaming({ id: c.id, name: c.name })}
                      >
                        <Pencil className='h-4 w-4' />
                      </Button>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        title='Delete category'
                        onClick={() => setDeleting(c)}
                      >
                        <Trash2 className='h-4 w-4 text-destructive' />
                      </Button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Delete confirmation */}
      {deleting && (
        <Dialog open onOpenChange={o => !o && setDeleting(null)}>
          <DialogContent className='sm:max-w-md'>
            <DialogHeader>
              <DialogTitle>Delete "{deleting.name}"?</DialogTitle>
              <DialogDescription>
                This removes the category and its grouping from{' '}
                {deleting.fieldCount} field{deleting.fieldCount === 1 ? '' : 's'}. The fields
                themselves are not deleted.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => setDeleting(null)}
                disabled={remove.isPending}
              >
                Cancel
              </Button>
              <Button
                type='button'
                variant='destructive'
                onClick={handleDelete}
                disabled={remove.isPending}
              >
                {remove.isPending ? 'Deleting…' : 'Delete category'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  )
}
